import { useCallback, useEffect, useState } from 'react';
import {
  ApiError,
  Badge,
  Button,
  Card,
  Empty,
  ErrorNote,
  Field,
  Input,
  Page,
  Spinner,
  Stars,
  api,
  apiRequest,
  cx,
  dateOnly,
  phone,
} from '@mobi/web';

type ApprovalStatus = 'pending' | 'approved' | 'rejected' | 'suspended';

interface Driver {
  id: number;
  name: string;
  email: string;
  phone: string;
  cpf: string;
  cnhNumber: string;
  cnhExpiresOn: string;
  approvalStatus: ApprovalStatus;
  rejectionReason: string | null;
  createdAt: string;
  rating: number | null;
  missing: string[];
  complete: boolean;
  documents: { cnh: boolean; selfie: boolean; crlv: boolean };
  vehicle: { id: number; type: 'car' | 'moto'; plate: string; model: string; color: string; year: number } | null;
}

const TABS: { value: ApprovalStatus; label: string }[] = [
  { value: 'pending', label: 'Pendentes' },
  { value: 'approved', label: 'Aprovados' },
  { value: 'rejected', label: 'Recusados' },
  { value: 'suspended', label: 'Suspensos' },
];

export function Queue() {
  const [status, setStatus] = useState<ApprovalStatus>('pending');
  const [drivers, setDrivers] = useState<Driver[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<Driver | null>(null);

  const load = useCallback(async (s: ApprovalStatus) => {
    setLoading(true);
    try {
      const res = await api<{ total: number; drivers: Driver[] }>(
        `/api/admin/drivers?status=${s}&limit=100`,
      );
      setDrivers(res.drivers);
      setTotal(res.total);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load(status);
    setSelected(null);
  }, [status, load]);

  if (selected) {
    return (
      <DriverDetail
        driver={selected}
        onBack={() => setSelected(null)}
        onChanged={() => {
          setSelected(null);
          void load(status);
        }}
      />
    );
  }

  return (
    <Page title="Fila de aprovação" subtitle="Documentos e dados de cada motorista, antes de liberar corridas.">
      <div className="mb-5 flex gap-1 overflow-x-auto">
        {TABS.map((t) => (
          <button
            key={t.value}
            type="button"
            onClick={() => setStatus(t.value)}
            className={cx(
              'shrink-0 rounded-xl px-4 py-2 text-sm font-medium transition',
              status === t.value ? 'bg-brand/15 text-brand' : 'bg-surface-2 text-muted',
            )}
          >
            {t.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="grid place-items-center py-16">
          <Spinner />
        </div>
      ) : drivers.length === 0 ? (
        <Empty title="Ninguém aqui" hint={`Nenhum motorista com status "${status}".`} />
      ) : (
        <Card className="divide-y divide-line p-0">
          {drivers.map((d) => (
            <button
              key={d.id}
              type="button"
              onClick={() => setSelected(d)}
              className="flex w-full items-center gap-4 px-5 py-4 text-left hover:bg-surface-2"
            >
              <div className="min-w-0 flex-1">
                <p className="font-medium">{d.name}</p>
                <p className="truncate text-sm text-muted">
                  {phone(d.phone)} · {d.vehicle ? `${d.vehicle.plate}` : 'sem veículo'}
                </p>
              </div>
              {!d.complete && status === 'pending' && (
                <Badge tone="warn">falta {d.missing.join(', ')}</Badge>
              )}
              <Stars value={d.rating} />
            </button>
          ))}
        </Card>
      )}
      <p className="mt-3 text-sm text-faint">{total} no total</p>
    </Page>
  );
}

const DOCS = [
  { kind: 'cnh', label: 'CNH' },
  { kind: 'selfie', label: 'Selfie' },
  { kind: 'crlv', label: 'CRLV' },
] as const;

function useAuthedFile(path: string | null): { url: string | null; isPdf: boolean } {
  const [url, setUrl] = useState<string | null>(null);
  const [isPdf, setIsPdf] = useState(false);

  useEffect(() => {
    if (!path) {
      setUrl(null);
      return;
    }
    let objectUrl: string | null = null;
    let cancelled = false;

    void (async () => {
      try {
        const res = await apiRequest(path);
        if (!res.ok) return;
        setIsPdf((res.headers.get('content-type') ?? '').includes('pdf'));
        const blob = await res.blob();
        if (cancelled) return;
        objectUrl = URL.createObjectURL(blob);
        setUrl(objectUrl);
      } catch {
        // documento indisponível — a tela mostra o aviso de "não enviado"
      }
    })();

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
      setUrl(null);
    };
  }, [path]);

  return { url, isPdf };
}

function DocViewer({ driverId, kind, label, sent }: { driverId: number; kind: string; label: string; sent: boolean }) {
  const [open, setOpen] = useState(false);
  const { url, isPdf } = useAuthedFile(open && sent ? `/api/admin/drivers/${driverId}/documents/${kind}` : null);

  return (
    <div className="rounded-2xl bg-surface-2 p-4">
      <div className="flex items-center justify-between gap-3">
        <p className="font-medium">
          {label} {sent ? <span className="text-good">✓ enviado</span> : <span className="text-danger">não enviado</span>}
        </p>
        {sent && (
          <Button variant="outline" onClick={() => setOpen((v) => !v)}>
            {open ? 'Ocultar' : 'Ver'}
          </Button>
        )}
      </div>
      {open && sent && (
        <div className="mt-3">
          {!url ? (
            <Spinner className="h-5 w-5" />
          ) : isPdf ? (
            <a href={url} target="_blank" rel="noreferrer" className="text-sm text-brand underline">
              abrir PDF em nova aba
            </a>
          ) : (
            <img src={url} alt={label} className="max-h-96 w-full rounded-xl object-contain" />
          )}
        </div>
      )}
    </div>
  );
}

function DriverDetail({
  driver,
  onBack,
  onChanged,
}: {
  driver: Driver;
  onBack: () => void;
  onChanged: () => void;
}) {
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [reason, setReason] = useState('');
  const [reasonFor, setReasonFor] = useState<'reject' | 'suspend' | null>(null);

  async function approve() {
    setBusy('approve');
    setError(null);
    try {
      await api(`/api/admin/drivers/${driver.id}/approve`, { method: 'POST' });
      onChanged();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'não foi possível aprovar');
    } finally {
      setBusy(null);
    }
  }

  async function submitReason() {
    if (!reasonFor || reason.trim().length < 5) return;
    setBusy(reasonFor);
    setError(null);
    try {
      await api(`/api/admin/drivers/${driver.id}/${reasonFor}`, { body: { reason } });
      onChanged();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'não foi possível concluir');
    } finally {
      setBusy(null);
    }
  }

  return (
    <Page title={driver.name}>
      <button type="button" onClick={onBack} className="mb-4 text-sm text-muted underline underline-offset-4">
        ← voltar à fila
      </button>

      {error && <ErrorNote>{error}</ErrorNote>}

      <div className="space-y-4">
        <Card>
          <p className="mb-3 font-semibold">Dados</p>
          <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
            <dt className="text-faint">E-mail</dt>
            <dd>{driver.email}</dd>
            <dt className="text-faint">Telefone</dt>
            <dd>{phone(driver.phone)}</dd>
            <dt className="text-faint">CPF</dt>
            <dd>{driver.cpf}</dd>
            <dt className="text-faint">CNH</dt>
            <dd>{driver.cnhNumber}</dd>
            <dt className="text-faint">CNH válida até</dt>
            <dd className={new Date(driver.cnhExpiresOn).getTime() < Date.now() ? 'text-danger' : undefined}>
              {dateOnly(driver.cnhExpiresOn)}
            </dd>
            <dt className="text-faint">Cadastrado em</dt>
            <dd>{dateOnly(driver.createdAt)}</dd>
          </dl>
        </Card>

        {driver.vehicle && (
          <Card>
            <p className="mb-3 font-semibold">Veículo</p>
            <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
              <dt className="text-faint">Tipo</dt>
              <dd>{driver.vehicle.type === 'car' ? 'Carro' : 'Moto'}</dd>
              <dt className="text-faint">Placa</dt>
              <dd>{driver.vehicle.plate}</dd>
              <dt className="text-faint">Modelo</dt>
              <dd>{driver.vehicle.model}</dd>
              <dt className="text-faint">Cor</dt>
              <dd>{driver.vehicle.color}</dd>
              <dt className="text-faint">Ano</dt>
              <dd>{driver.vehicle.year}</dd>
            </dl>
          </Card>
        )}

        <Card>
          <p className="mb-3 font-semibold">Documentos</p>
          <div className="space-y-3">
            {DOCS.map((doc) => (
              <DocViewer
                key={doc.kind}
                driverId={driver.id}
                kind={doc.kind}
                label={doc.label}
                sent={driver.documents[doc.kind]}
              />
            ))}
          </div>
        </Card>

        {driver.rejectionReason && (
          <Card tone="danger">
            <p className="font-semibold text-danger">Motivo do último status</p>
            <p className="mt-1 text-sm">{driver.rejectionReason}</p>
          </Card>
        )}

        {driver.approvalStatus === 'pending' && (
          <Card>
            <p className="mb-3 font-semibold">Decisão</p>
            {!driver.complete && (
              <p className="mb-3 text-sm text-warn">
                Cadastro incompleto — falta {driver.missing.join(', ')}. Aprovar fica bloqueado até chegar tudo.
              </p>
            )}
            <div className="flex flex-wrap gap-3">
              <Button onClick={approve} loading={busy === 'approve'} disabled={!driver.complete}>
                Aprovar
              </Button>
              <Button variant="outline" onClick={() => setReasonFor('reject')}>
                Recusar
              </Button>
            </div>
          </Card>
        )}

        {driver.approvalStatus === 'approved' && (
          <Card>
            <p className="mb-3 font-semibold">Decisão</p>
            <Button variant="danger" onClick={() => setReasonFor('suspend')}>
              Suspender
            </Button>
          </Card>
        )}

        {reasonFor && (
          <Card tone={reasonFor === 'reject' ? 'warn' : 'danger'}>
            <Field label={reasonFor === 'reject' ? 'Motivo da recusa' : 'Motivo da suspensão'} hint="mínimo 5 caracteres, o motorista vê isto">
              <Input value={reason} onChange={(e) => setReason(e.target.value)} autoFocus />
            </Field>
            <div className="mt-3 flex gap-3">
              <Button
                variant={reasonFor === 'reject' ? 'outline' : 'danger'}
                loading={busy === reasonFor}
                disabled={reason.trim().length < 5}
                onClick={submitReason}
              >
                Confirmar {reasonFor === 'reject' ? 'recusa' : 'suspensão'}
              </Button>
              <Button variant="ghost" onClick={() => { setReasonFor(null); setReason(''); }}>
                Cancelar
              </Button>
            </div>
          </Card>
        )}
      </div>
    </Page>
  );
}
